import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { URL_WOOD_EXPORT } from '../../constance/urls';
import { DELETE, GET } from '../../utils/HttpClient';
import { ellipsisString, formatComma } from '../../utils/stringFormat';
import { Toast } from '../../utils/Toast';
import ConfirmModal from '../common/ConfirmModal';
import AddExportBarcodeModal from './AddExportBarcodeModal';
import Title from 'antd/es/typography/Title';
import { Alert, Spin, Form, Input, Card, Space, Button, Table, List, Tag, Col, Row, Divider, Flex } from 'antd';
import { PlusOutlined, DeleteFilled } from '@ant-design/icons'
import '../../antd.css'
import { BLUE_COLOR, GREEN } from '../../constance/color';
import ColorButton from '../common/ColorButton';

export default function ExportItemList(props) {
  const {
    editable,
    exportTransactionId
  } = props;

  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessages, setErrorMessages] = React.useState(null);
  const [saleLineList, setSaleLineList] = React.useState([]);
  const [saleLineTarget, setSaleLineTarget] = React.useState(null);
  const [deleteTarget, setDeleteTarget] = React.useState(null);

  const fetchData = async () => {
    setIsLoading(true);
    setErrorMessages(null);
    try {
      const response = await GET(`${URL_WOOD_EXPORT.EXPORT_TRANSACTION}${exportTransactionId}${URL_WOOD_EXPORT.EXPORT_SALE_LINES}`)
      setSaleLineList(response.data)
    } catch (error) {
      setErrorMessages(error.errorMessages)
    } finally {
      setIsLoading(false);
    }
  }

  const deleteData = async (target) => {
    setIsLoading(true);
    try {
      await DELETE(`${URL_WOOD_EXPORT.EXPORT_ITEM}${target}/`)
      Toast.success(`${t('delete')} ${t('success')}`)
      fetchData();
    } catch (error) {
      Toast.error(error.errorMessages)
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, [exportTransactionId])

  return (
    <div>
      <Title level={3}>{t('export_transactions.sale_line')}</Title>
      {errorMessages &&
        <Alert
          type="error"
          message={t('error')}
          description={errorMessages} />
      }
      <Spin spinning={isLoading} fullscreen size='large' />
      {saleLineList.map(saleLine => (
        <div>
          <Card style={{ borderBlockStartColor: BLUE_COLOR }}>
            <Flex justify="space-between">
              <Space direction="vertical">
                <label style={{ fontWeight: "bolder" }}>{t('export_transactions.product_code')}</label>
                <div>{_.get(saleLine, 'product_code', '-') || '-'}</div>
              </Space>
              <Space direction="vertical">
                <label style={{ fontWeight: "bolder" }}>{t('export_transactions.product_name')}</label>
                <div>{_.get(saleLine, 'product_name', '-') || '-'}</div>
              </Space>
              <Space direction="vertical">
                <label style={{ fontWeight: "bolder" }}>{t('export_transactions.lot_id')}</label>
                <div>{_.get(saleLine, 'lot_id', '-') || '-'}</div>
              </Space>
              <Space direction="vertical">
                <label style={{ fontWeight: "bolder" }}>{t('export_transactions.soj')}</label>
                <div>{_.get(saleLine, 'soj', '-') || '-'}</div>
              </Space>
              <Space direction="vertical">
                <label style={{ fontWeight: "bolder" }}>{t('export_transactions.pallet')}</label>
                <div>{formatComma(_.get(saleLine, 'pallet', 0)) || '-'}</div>
              </Space>
              <Space direction="vertical">
                <label style={{ fontWeight: "bolder" }}>{t('export_transactions.amount')}</label>
                <div>{formatComma(_.get(saleLine, 'amount', 0)) || '-'}</div>
              </Space>
              <Space direction="vertical">
                <label style={{ fontWeight: "bolder" }}>{_.get(saleLine, 'sale_unit') || '-'}</label>
                <div>{formatComma(_.get(saleLine, 'volume', 0)) || '-'}</div>
              </Space>
            </Flex>

            <Divider />
            
            <Col sm={24} md={8} lg={4} style={{ width: '100%' }}>
            {editable &&
              <Form.Item label={t('barcodes.barcode_id')} name={'button'} layout='horizontal'>
                  <ColorButton
                    type='primary'
                    override={GREEN}
                    icon={<PlusOutlined />}
                    onClick={() => setSaleLineTarget(saleLine.id)}
                    block >
                    {t('add')}</ColorButton>
              </Form.Item>
              }
            </Col>


            {_.get(saleLine, 'export_items', []).length > 0 ? (
              <Col sm={24} md={24} lg={24} style={{ width: '100%' }}>
                <Form.Item className="exportItemList-table">
                  <Table
                    scroll={{ x: 'max-content' }}
                    columns={[
                      {
                        title: t('barcodes.barcode_id'),
                        dataIndex: 'barcode_no',
                        key: 'barcode_no',
                        render: barcode_no => barcode_no || '-',
                      },
                      {
                        title: t('export_transactions.product_code'),
                        dataIndex: 'wood_size_amount_map_list',
                        key: 'product_code',
                        render: woodSizeList =>
                          woodSizeList && woodSizeList.length > 0 ? (
                            <List
                              dataSource={woodSizeList}
                              renderItem={item => (
                                <List.Item>
                                  <Space>
                                    {_.get(item, 'wood_code', '')} x {formatComma(_.get(item, 'amount', 0))}

                                    <Tag color="blue">
                                      {formatComma(_.get(item, 'volume', 0))} {t('units.cubic')} {t('units.ft')}
                                    </Tag>
                                  </Space>
                                </List.Item>
                              )}
                            />
                          ) : null,

                      },
                      {
                        title: t('export_transactions.packing_type'),
                        dataIndex: 'packing_type',
                        key: 'packing_type',
                        render: packingType => (packingType ? t(`export_transactions.packing_types.${packingType}`) : '-'),
                      },
                      {
                        title: t('export_transactions.humidity'),
                        dataIndex: 'humidity',
                        key: 'humidity',
                      },
                      {
                        title: () => null,
                        dataIndex: 'id',
                        key: 'actions',
                        render: (__, record) => (
                          <div style={{ textAlign: 'center' }}>
                            {editable &&
                              <Button
                                icon={<DeleteFilled />}
                                danger
                                onClick={() => setDeleteTarget(record.id)}
                              >
                                {t('delete')}
                              </Button>
                            }
                          </div>
                        ),
                      },
                    ]}
                    dataSource={_.get(saleLine, 'export_items', []).map((exportItem, index) => ({
                      key: index,
                      ...exportItem,
                    }))}
                    pagination={{
                      size: "small",
                      showSizeChanger: true,
                      position: ["bottomCenter"],
                      showTotal: (total) => `Total ${total} rows`,
                    }}

                  />
                </Form.Item>
              </Col>

            ) : null}

          </Card>
        </div>
      ))}

      <AddExportBarcodeModal
        saleLineTarget={saleLineTarget}
        open={saleLineTarget != null}
        onClose={() => setSaleLineTarget(null)}
        fetchData={fetchData} />

      <ConfirmModal
        open={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        title={t('confirms.delete_title')}
        content={t('confirms.delete_content')}
        onConfirm={() => {
          deleteData(deleteTarget);
          setDeleteTarget(null);
        }}
      />

    </div>
  )
}